/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   แก้ไข Template เลือกวัสดุ   /master/template/manage/:code  (new = สร้างใหม่)
   จุดที่ต้องเลือก (ห้อง/พื้นที่ + หมวด) → ตัวเลือกวัสดุ standard / upgrade + ส่วนต่างราคา
============================================================ */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Form,
  Image,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CalculatorOutlined,
  CopyOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveFilled,
} from "@ant-design/icons";
import { ButtonBack } from "../../../components/button";
import { TemplateService, apiError } from "../../../service/Ak.service";
import OptionService from "../../../service/Options.service";
import { usePermission } from "../../../store/context/permission-context";
import { fileUrl, formatMoney } from "../../../utils/util";

const opt = OptionService();
const BACK = "/master/template";
let seqKey = 1;
const newKey = () => `k${Date.now()}${seqKey++}`;
const money = { formatter: (v) => (v === undefined || v === null || v === "" ? "" : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")), parser: (v) => `${v}`.replace(/,/g, "") };

const AREA_OPTIONS = ["ทั้งหลัง", "ห้องนั่งเล่น", "ห้องรับประทานอาหาร", "ห้องครัว", "ห้องนอนใหญ่", "ห้องนอน 2", "ห้องนอน 3", "ห้องน้ำ 1", "ห้องน้ำ 2", "ห้องน้ำ 3", "โถงบันได", "ระเบียง", "ที่จอดรถ", "ภายนอกอาคาร"].map((v) => ({ value: v, label: v }));

const emptyItem = () => ({ key: newKey(), area: null, cat_code: null, item_name: "", qty: null, unit: null, remark: null, options: [] });

export default function TemplateManage() {
  const { code } = useParams();
  const isNew = code === "new";
  const navigate = useNavigate();
  const { can } = usePermission();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([emptyItem()]);
  const [cats, setCats] = useState([]);
  const [models, setModels] = useState([]);
  const [mats, setMats] = useState([]);
  const [info, setInfo] = useState(null);

  const readOnly = !can("master_template", isNew ? "add" : "edit");
  const matMap = useMemo(() => new Map(mats.map((m) => [m.mat_code, m])), [mats]);

  useEffect(() => {
    Promise.all([opt.categories(), opt.houseModels(), opt.materials()]).then(([c, h, m]) => {
      setCats(c.data?.data || []);
      setModels(h.data?.data || []);
      setMats(m.data?.data || []);
    });
    if (!isNew) {
      TemplateService.get(code)
        .then((res) => {
          const t = res.data?.data;
          setInfo(t);
          form.setFieldsValue({ tpl_name: t.tpl_name, model_code: t.model_code, remark: t.remark, active_status: t.active_status });
          setItems(
            (t.items || []).map((it) => ({
              ...it,
              key: `i${it.item_id}`,
              options: (it.options || []).map((o) => ({ ...o, key: `o${o.option_id}` })),
            }))
          );
        })
        .catch((e) => message.error(apiError(e, "โหลด Template ไม่สำเร็จ")))
        .finally(() => setLoading(false));
    } else {
      form.setFieldsValue({ active_status: "Y" });
    }
  }, [code]);

  /* ---------- แก้ไขรายการ ---------- */
  const setItem = (key, patch) => setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  const move = (idx, dir) =>
    setItems((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  const removeItem = (key) => setItems((prev) => prev.filter((it) => it.key !== key));
  const dupItem = (idx) =>
    setItems((prev) => {
      const src = prev[idx];
      const copy = { ...src, key: newKey(), item_id: undefined, options: src.options.map((o) => ({ ...o, key: newKey(), option_id: undefined })) };
      return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
    });

  const addOption = (item, mat_code) => {
    if (!mat_code) return;
    if (item.options.some((o) => o.mat_code === mat_code)) return message.warning("มีวัสดุนี้ในจุดนี้แล้ว");
    const first = item.options.length === 0;
    setItem(item.key, {
      options: [
        ...item.options,
        { key: newKey(), mat_code, option_type: first ? "standard" : "upgrade", price_diff: 0, is_default: first ? "Y" : "N" },
      ],
      /* จุดที่ยังไม่มีหมวด ใช้หมวดของวัสดุตัวแรก */
      cat_code: item.cat_code || matMap.get(mat_code)?.cat_code || null,
      unit: item.unit || null,
    });
  };
  const setOption = (item, okey, patch) =>
    setItem(item.key, {
      options: item.options.map((o) => {
        if (patch.is_default === "Y") return o.key === okey ? { ...o, ...patch } : { ...o, is_default: "N" };
        return o.key === okey ? { ...o, ...patch } : o;
      }),
    });
  const removeOption = (item, okey) => setItem(item.key, { options: item.options.filter((o) => o.key !== okey) });

  /* ส่วนต่าง = (ราคาวัสดุตัวนี้ - ราคาวัสดุมาตรฐาน) x จำนวน */
  const calcDiff = (item) => {
    const std = item.options.find((o) => o.option_type === "standard");
    const stdPrice = Number(matMap.get(std?.mat_code)?.price || 0);
    const qty = Number(item.qty || 1);
    setItem(item.key, {
      options: item.options.map((o) =>
        o.option_type === "upgrade"
          ? { ...o, price_diff: Math.max(0, Math.round((Number(matMap.get(o.mat_code)?.price || 0) - stdPrice) * qty)) }
          : o
      ),
    });
    message.info("คำนวณจากราคาวัสดุ × จำนวนแล้ว ปรับเองได้");
  };

  /* ---------- บันทึก ---------- */
  const save = async () => {
    const h = await form.validateFields();
    const bad = items.find((it) => !String(it.item_name || "").trim() || it.options.length === 0);
    if (bad) return message.error(`"${bad.item_name || "(ยังไม่ตั้งชื่อ)"}" ต้องมีชื่อจุดและตัวเลือกวัสดุอย่างน้อย 1 ตัว`);
    const body = {
      header: { ...h, tpl_code: isNew ? undefined : code },
      items: items.map((it) => ({
        item_id: it.item_id,
        area: it.area,
        cat_code: it.cat_code,
        item_name: it.item_name,
        qty: it.qty,
        unit: it.unit,
        remark: it.remark,
        options: it.options.map((o) => ({ mat_code: o.mat_code, option_type: o.option_type, price_diff: o.price_diff || 0, is_default: o.is_default })),
      })),
    };
    setSaving(true);
    try {
      const res = await (isNew ? TemplateService.create(body) : TemplateService.update(body));
      message.success("บันทึก Template เรียบร้อย");
      if (isNew) navigate(`${BACK}/manage/${res.data?.data?.tpl_code}`, { replace: true });
    } catch (e) {
      message.error(apiError(e, "บันทึกไม่สำเร็จ"));
    } finally {
      setSaving(false);
    }
  };

  const matOptionsFor = (item) => {
    const list = item.cat_code ? mats.filter((m) => m.cat_code === item.cat_code) : mats;
    return list.map((m) => ({ value: m.mat_code, label: `${m.mat_name}${m.brand ? ` · ${m.brand}` : ""}${m.price ? ` (${formatMoney(m.price, 0)}/${m.unit || "-"})` : ""}` }));
  };

  const optionColumns = (item) => [
    {
      title: "วัสดุ",
      dataIndex: "mat_code",
      render: (v) => {
        const m = matMap.get(v) || {};
        return (
          <Flex gap={8} align="center">
            {m.image_path ? <Image src={fileUrl(m.image_path)} width={40} height={40} style={{ objectFit: "cover", borderRadius: 4 }} /> : <div className="ak-img-empty sm" />}
            <div>
              <div>{m.mat_name || v}</div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {[m.brand, m.model, m.color].filter(Boolean).join(" · ") || v}
                {m.price ? ` · ${formatMoney(m.price, 0)}/${m.unit || "-"}` : ""}
              </Typography.Text>
            </div>
          </Flex>
        );
      },
    },
    {
      title: "ประเภท",
      dataIndex: "option_type",
      width: 170,
      render: (v, o) => (
        <Radio.Group
          size="small"
          disabled={readOnly}
          value={v}
          optionType="button"
          onChange={(e) => setOption(item, o.key, { option_type: e.target.value, price_diff: e.target.value === "standard" ? 0 : o.price_diff })}
          options={[
            { value: "standard", label: "มาตรฐาน" },
            { value: "upgrade", label: "อัปเกรด" },
          ]}
        />
      ),
    },
    {
      title: "ส่วนต่าง (บาท)",
      dataIndex: "price_diff",
      width: 150,
      render: (v, o) =>
        o.option_type === "standard" ? (
          <Typography.Text type="secondary">รวมในราคาบ้าน</Typography.Text>
        ) : (
          <InputNumber size="small" disabled={readOnly} value={v} style={{ width: "100%" }} {...money} onChange={(val) => setOption(item, o.key, { price_diff: val })} />
        ),
    },
    {
      title: "ค่าเริ่มต้น",
      dataIndex: "is_default",
      width: 90,
      align: "center",
      render: (v, o) => <Radio disabled={readOnly} checked={v === "Y"} onChange={() => setOption(item, o.key, { is_default: "Y" })} />,
    },
    {
      title: "",
      key: "x",
      width: 40,
      render: (_, o) => !readOnly && <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeOption(item, o.key)} />,
    },
  ];

  if (loading) return <Flex justify="center" style={{ padding: 80 }}><Spin /></Flex>;

  return (
    <div className="template-manage">
      <Flex justify="space-between" align="center" className="mb-3" wrap="wrap" gap={8}>
        <Typography.Title level={3} className="m-0 !text-zinc-800">
          {isNew ? "สร้าง Template เลือกวัสดุ" : `Template ${code}`}
        </Typography.Title>
        <Space>
          <ButtonBack target={BACK} />
          {!readOnly && (
            <Button type="primary" icon={<SaveFilled />} loading={saving} onClick={save}>
              บันทึก
            </Button>
          )}
        </Space>
      </Flex>

      {info?.used_count > 0 && (
        <Alert className="mb-3" type="info" showIcon message="Template นี้ถูกใช้ในใบเลือกวัสดุแล้ว — ใบที่ล็อกสเปกไปแล้วไม่เปลี่ยนตาม" />
      )}

      <Card className="ak-card mb-3" title="ข้อมูล Template">
        <Form form={form} layout="vertical" disabled={readOnly}>
          <Row gutter={12}>
            <Col xs={24} md={10}>
              <Form.Item name="tpl_name" label="ชื่อ Template" rules={[{ required: true, message: "กรอกชื่อ Template" }]}>
                <Input maxLength={200} placeholder="เช่น ชุดมาตรฐาน AK-101" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="model_code" label="ใช้กับแบบบ้าน">
                <Select allowClear options={models} showSearch optionFilterProp="label" placeholder="ทุกแบบ" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="active_status" label="สถานะ">
                <Select options={[{ value: "Y", label: "ใช้งาน" }, { value: "N", label: "ปิดใช้งาน" }]} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="remark" label="หมายเหตุ" className="!mb-0">
                <Input.TextArea rows={2} maxLength={2000} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Flex justify="space-between" align="center" className="mb-2">
        <Typography.Title level={4} className="m-0">
          จุดที่ให้ลูกค้าเลือก ({items.length})
        </Typography.Title>
        {!readOnly && (
          <Button icon={<PlusOutlined />} onClick={() => setItems((p) => [...p, emptyItem()])}>
            เพิ่มจุด
          </Button>
        )}
      </Flex>

      {items.length === 0 && <Card><Empty description="ยังไม่มีจุดที่ให้เลือก" /></Card>}

      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {items.map((it, idx) => (
          <Card
            key={it.key}
            size="small"
            className="ak-card"
            title={
              <Space>
                <Tag color="gold">{idx + 1}</Tag>
                <span>{[it.area, it.item_name].filter(Boolean).join(" · ") || "จุดใหม่"}</span>
                {it.options.length === 0 && <Tag color="red">ยังไม่มีตัวเลือก</Tag>}
              </Space>
            }
            extra={
              !readOnly && (
                <Space size={2}>
                  <Tooltip title="เลื่อนขึ้น"><Button size="small" type="text" icon={<ArrowUpOutlined />} onClick={() => move(idx, -1)} /></Tooltip>
                  <Tooltip title="เลื่อนลง"><Button size="small" type="text" icon={<ArrowDownOutlined />} onClick={() => move(idx, 1)} /></Tooltip>
                  <Tooltip title="ทำสำเนา"><Button size="small" type="text" icon={<CopyOutlined />} onClick={() => dupItem(idx)} /></Tooltip>
                  <Popconfirm title="ลบจุดนี้?" onConfirm={() => removeItem(it.key)} okText="ลบ" cancelText="ไม่">
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              )
            }
          >
            <Row gutter={8}>
              <Col xs={24} md={5}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>ห้อง / พื้นที่</Typography.Text>
                <AutoComplete disabled={readOnly} value={it.area} onChange={(v) => setItem(it.key, { area: v })} options={AREA_OPTIONS} allowClear style={{ width: "100%" }}
                  placeholder="เลือกหรือพิมพ์" filterOption={(input, o) => String(o.value).includes(input)} />
              </Col>
              <Col xs={24} md={5}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>หมวดวัสดุ</Typography.Text>
                <Select disabled={readOnly} value={it.cat_code} onChange={(v) => setItem(it.key, { cat_code: v })} options={cats} allowClear style={{ width: "100%" }} />
              </Col>
              <Col xs={24} md={6}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>ชื่อจุด *</Typography.Text>
                <Input disabled={readOnly} value={it.item_name} maxLength={200} onChange={(e) => setItem(it.key, { item_name: e.target.value })} placeholder="เช่น กระเบื้องพื้น" status={!String(it.item_name || "").trim() ? "warning" : undefined} />
              </Col>
              <Col xs={12} md={3}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>จำนวน</Typography.Text>
                <InputNumber disabled={readOnly} value={it.qty} min={0} style={{ width: "100%" }} onChange={(v) => setItem(it.key, { qty: v })} />
              </Col>
              <Col xs={12} md={2}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>หน่วย</Typography.Text>
                <Input disabled={readOnly} value={it.unit} maxLength={30} onChange={(e) => setItem(it.key, { unit: e.target.value })} placeholder="ตร.ม." />
              </Col>
              <Col xs={24} md={3}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>หมายเหตุ</Typography.Text>
                <Input disabled={readOnly} value={it.remark} maxLength={255} onChange={(e) => setItem(it.key, { remark: e.target.value })} />
              </Col>
            </Row>

            <Table
              className="mt-3"
              size="small"
              rowKey="key"
              pagination={false}
              columns={optionColumns(it)}
              dataSource={it.options}
              locale={{ emptyText: "เพิ่มวัสดุที่ให้ลูกค้าเลือกด้านล่าง" }}
            />
            {!readOnly && (
              <Flex gap={8} className="mt-2" wrap="wrap">
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={`+ เพิ่มตัวเลือกวัสดุ${it.cat_code ? " (เฉพาะหมวดนี้)" : ""}`}
                  value={null}
                  onChange={(v) => addOption(it, v)}
                  options={matOptionsFor(it)}
                  style={{ minWidth: 320, flex: 1 }}
                />
                <Tooltip title="ส่วนต่าง = (ราคาตัวเลือก - ราคามาตรฐาน) × จำนวน">
                  <Button icon={<CalculatorOutlined />} disabled={it.options.length < 2} onClick={() => calcDiff(it)}>
                    คำนวณส่วนต่าง
                  </Button>
                </Tooltip>
              </Flex>
            )}
          </Card>
        ))}
      </Space>

      {!readOnly && items.length > 0 && (
        <Flex justify="end" className="mt-3" gap={8}>
          <Button icon={<PlusOutlined />} onClick={() => setItems((p) => [...p, emptyItem()])}>
            เพิ่มจุด
          </Button>
          <Button type="primary" icon={<SaveFilled />} loading={saving} onClick={save}>
            บันทึก
          </Button>
        </Flex>
      )}
    </div>
  );
}
